/**
 * The rules of an artist address (the account slug of `<slug>.music24.link`). The web app, the
 * welcome page and music24-api (`POST /accountNames/verifySlug`, `onboarding.saveIdentity`) read
 * this one file (QA 2026-10-28, brk-3 and brk-14). This file has no imports, so every project may
 * read it.
 */
export const ACCOUNT_SLUG_MIN_LENGTH = 2;
export const ACCOUNT_SLUG_MAX_LENGTH = 60;

/**
 * Addresses that Music24 keeps for its own hosts, now or later. `demo` is the demo artist and
 * `pitch` the pitch pages. An account that already holds one keeps it; nobody new can take one.
 */
export const RESERVED_ACCOUNT_SLUGS: readonly string[] = [
  'demo',
  'pitch',
  'www',
  'api',
  'app',
  'apps',
  'admin',
  'mail',
  'email',
  'smtp',
  'imap',
  'mx',
  'ns1',
  'ns2',
  'link',
  'links',
  'help',
  'support',
  'status',
  'static',
  'cdn',
  'assets',
  'files',
  'img',
  'images',
  'media',
  'share',
  'embed',
  'docs',
  'blog',
  'auth',
  'login',
  'signup',
  'account',
  'accounts',
  'settings',
  'dev',
  'staging',
  'test',
  'rc',
  'beta',
  'music24',
  'imgproxy',
];

/** Why an address cannot be used: `invalid` (characters, dashes or length) or `reserved`. */
export type AccountSlugProblem = 'invalid' | 'reserved';

/**
 * The exact reason why an address cannot be used (QA 2026-10-28 brk-r2-4). Each reason has its own
 * message in the apps:
 * - `tooShort`: fewer than `ACCOUNT_SLUG_MIN_LENGTH` characters;
 * - `tooLong`: more than `ACCOUNT_SLUG_MAX_LENGTH` characters;
 * - `characters`: a character that is not a lower-case letter, a digit, `_` or `-`, two dashes in a
 *   row, or a dash at the start or the end;
 * - `reserved`: Music24 keeps the address for its own hosts.
 */
export type AccountSlugRefusal = 'tooShort' | 'tooLong' | 'characters' | 'reserved';

export const ACCOUNT_SLUG_REFUSALS: readonly AccountSlugRefusal[] = ['tooShort', 'tooLong', 'characters', 'reserved'];

/**
 * The characters of an address: lower-case letters, digits, `_` and single dashes, with no dash at
 * the start or the end (a DNS label cannot start or end with a dash).
 */
const ACCOUNT_SLUG_PATTERN = /^[a-z0-9_](?:[a-z0-9_]|-(?!-))*$/;

/** The exact reason why an address cannot be used, or null when it can be used. */
export function accountSlugRefusal(slug: string): AccountSlugRefusal | null {
  if (slug.length < ACCOUNT_SLUG_MIN_LENGTH) {
    return 'tooShort';
  }
  if (slug.length > ACCOUNT_SLUG_MAX_LENGTH) {
    return 'tooLong';
  }
  if (!ACCOUNT_SLUG_PATTERN.test(slug) || slug.endsWith('-')) {
    return 'characters';
  }
  return RESERVED_ACCOUNT_SLUGS.includes(slug) ? 'reserved' : null;
}

/** The problem of an address, or null when it can be used. `accountSlugRefusal` gives the exact reason. */
export function accountSlugProblem(slug: string): AccountSlugProblem | null {
  const refusal = accountSlugRefusal(slug);
  if (refusal === null) {
    return null;
  }
  return refusal === 'reserved' ? 'reserved' : 'invalid';
}

/**
 * An address made from free text, cut to the longest length with no dash at the start or the end.
 * It does not change the characters: pass a slugified value.
 */
export function fitAccountSlug(slug: string): string {
  return slug.replace(/^-+/, '').slice(0, ACCOUNT_SLUG_MAX_LENGTH).replace(/-+$/, '');
}

/**
 * A usable address made from a slugified value: no double dash, cut to the longest length, and
 * filled up to the shortest length with `1` (`a` → `a1`). It does not check `reserved` or `taken`.
 */
export function usableAccountSlugBase(slug: string): string {
  const fitted = fitAccountSlug(slug.replace(/-{2,}/g, '-'));
  if (fitted === '') {
    return 'artist';
  }
  if (fitted.length >= ACCOUNT_SLUG_MIN_LENGTH) {
    return fitted;
  }
  return `${fitted}${'1'.repeat(ACCOUNT_SLUG_MIN_LENGTH - fitted.length)}`;
}
